/** @jsxImportSource theme-ui */
import React, { useState } from 'react';
import { Box, Checkbox, Flex, Label, Select, Switch, Text, Textarea } from 'theme-ui';
import { Field } from 'formik';
import { JsonEditor } from 'json-edit-react';
import JsonFieldViewer from '@/components/JsonFieldViewer';
import ColorHelperPanel from '@/components/ColorHelperPanel';
import Button from '@/components/Button';
import WidgetPreviewPane from './WidgetPreviewPane';
import RichSelect from '@/components/Select';
import { ensureGoogleFontsLoaded, extractFamilyName } from '@/utils/googleFonts';
import { buildFullEmbedScript } from './embedScript';

export type JsonSchemaNode = {
    $ref?: string;
    type?: string;
    enum?: unknown[];
    properties?: Record<string, JsonSchemaNode>;
    items?: JsonSchemaNode;
    [key: string]: unknown;
};

// Schemas name enum options via `x-enum-names`, either a plain array positional with
// `enum`, or an object keyed by language code (each an array positional with `enum`).
// This tool has no locale selector, so it always reads the "en" entry.
export const getEnumLabel = (node: JsonSchemaNode | undefined, index: number, rawValue: unknown): string => {
    const raw = node?.['x-enum-names'];
    const names = Array.isArray(raw)
        ? raw
        : raw && typeof raw === 'object'
            ? (raw as Record<string, unknown[]>).en
            : undefined;
    const label = names?.[index];
    return typeof label === 'string' ? label : String(rawValue);
};

const resolveRefPointer = (
    root: Record<string, unknown> | undefined,
    ref: string
): JsonSchemaNode | undefined => {
    if (!root || !ref.startsWith('#/')) return undefined;
    let node: unknown = root;
    for (const segment of ref.slice(2).split('/')) {
        if (node == null || typeof node !== 'object') return undefined;
        node = (node as Record<string, unknown>)[segment];
    }
    return node as JsonSchemaNode | undefined;
};

// The widget-configuration-schema API sometimes serializes nested schema fragments
// (individual $defs entries, their properties) as JSON-encoded strings instead of
// objects — apiService.tsx already works around this for the schema's own top-level
// properties/$defs/required, but not recursively for what those contain.
const normalizeNode = (node: unknown): JsonSchemaNode | undefined => {
    if (typeof node !== 'string') return node as JsonSchemaNode | undefined;
    try {
        const parsed = JSON.parse(node);
        return parsed && typeof parsed === 'object' ? (parsed as JsonSchemaNode) : undefined;
    } catch {
        return undefined;
    }
};

// Local schemas (e.g. the widget config v7 schema) define every reusable enum/object
// under $defs and reference it via $ref instead of inlining type/enum/properties.
const dereference = (
    root: Record<string, unknown> | undefined,
    node: JsonSchemaNode | undefined
): JsonSchemaNode | undefined => {
    let current = normalizeNode(node);
    for (let i = 0; i < 10 && current?.$ref; i++) {
        current = normalizeNode(resolveRefPointer(root, current.$ref));
    }
    return current;
};

export const resolveSchemaNode = (
    schema: Record<string, unknown> | undefined,
    path: (string | number)[]
): JsonSchemaNode | undefined => {
    let node = dereference(schema, schema as JsonSchemaNode | undefined);
    for (const segment of path) {
        if (!node) return undefined;
        if (typeof segment === 'number') {
            node = node.items;
        } else if (node.properties) {
            node = node.properties[segment];
        } else {
            return undefined;
        }
        node = dereference(schema, node);
    }
    return node;
};

const FONT_PREVIEW_SAMPLE = 'The quick brown fox jumps over the lazy dog';

const formatFontMenuLabel = (fontName: string): string => `${FONT_PREVIEW_SAMPLE} (${fontName})`;

export const validateConfigurationJson = (value: string): string | undefined => {
    if (!value.trim()) return 'Required';
    try {
        JSON.parse(value);
    } catch {
        return 'Invalid JSON';
    }
    return undefined;
};

interface Props {
    name: string;
    rows?: number;
    placeholder?: string;
    schema?: Record<string, unknown>;
    companySitepath?: string;
    embedScriptUrl?: string;
    configId?: string;
    fitViewportEnabled?: boolean;
    onFitViewportEnabledChange?: (checked: boolean) => void;
}

const COPIED_FEEDBACK_MS = 2000;

const ConfigurationJsonField: React.FC<Props> = ({
    name,
    rows = 12,
    placeholder,
    schema,
    companySitepath,
    embedScriptUrl,
    configId,
    fitViewportEnabled: fitViewportEnabledProp,
    onFitViewportEnabledChange,
}) => {
    const [showPreview, setShowPreview] = useState(true);
    const [copiedFullScript, setCopiedFullScript] = useState(false);
    const [fitViewportEnabledState, setFitViewportEnabledState] = useState(true);
    // Uncontrolled fallback for the "create configuration" usage, which has no
    // row-level checkbox to sync with (no configId yet, so canCopyFullScript
    // is false there and this control never renders).
    const fitViewportEnabled = fitViewportEnabledProp ?? fitViewportEnabledState;
    const setFitViewportEnabled = onFitViewportEnabledChange ?? setFitViewportEnabledState;
    const canPreview = Boolean(companySitepath && embedScriptUrl);
    const canCopyFullScript = Boolean(companySitepath && embedScriptUrl && configId);
    const customNodeDefinitions = schema
        ? [
            {
                condition: ({ path }: { path: (string | number)[] }) =>
                    resolveSchemaNode(schema, path)?.type === 'boolean',
                element: ({ value, handleEdit, canEdit }: any) => (
                    <Switch
                        checked={Boolean(value)}
                        disabled={!canEdit}
                        onChange={() => handleEdit(!value)}
                        sx={{ flex: '0 0 auto', width: '40px' }}
                    />
                ),
                showOnView: true,
            },
            {
                // Whole array of enum values (e.g. behavior.bookMethods) — a checkbox
                // group toggling membership, not a per-item dropdown.
                condition: ({ path }: { path: (string | number)[] }) =>
                    resolveSchemaNode(schema, path)?.type === 'array' &&
                    Array.isArray(resolveSchemaNode(schema, [...path, 0])?.enum),
                element: ({ value, handleEdit, canEdit, nodeData }: any) => {
                    const itemSchema = resolveSchemaNode(schema, [...nodeData.path, 0]);
                    const options = itemSchema?.enum ?? [];
                    const current: string[] = Array.isArray(value) ? value : [];
                    return (
                        <Flex sx={{ flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
                            {options.map((opt: unknown, index: number) => {
                                const optValue = String(opt);
                                const checked = current.includes(optValue);
                                return (
                                    <Label
                                        key={optValue}
                                        sx={{ width: 'auto', alignItems: 'center', gap: 1 }}
                                    >
                                        <Checkbox
                                            checked={checked}
                                            disabled={!canEdit}
                                            onChange={() =>
                                                handleEdit(
                                                    checked
                                                        ? current.filter((v) => v !== optValue)
                                                        : [...current, optValue]
                                                )
                                            }
                                        />
                                        {getEnumLabel(itemSchema, index, opt)}
                                    </Label>
                                );
                            })}
                        </Flex>
                    );
                },
                showOnView: true,
                renderCollectionAsValue: true,
            },
            {
                condition: ({ path }: { path: (string | number)[] }) =>
                    Array.isArray(resolveSchemaNode(schema, path)?.enum),
                element: ({ value, handleEdit, canEdit, nodeData }: any) => {
                    const itemSchema = resolveSchemaNode(schema, nodeData.path);
                    const options = itemSchema?.enum ?? [];
                    return (
                        <Select
                            value={String(value)}
                            disabled={!canEdit}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                handleEdit(e.target.value)
                            }
                        >
                            {options.map((opt: unknown, index: number) => (
                                <option key={String(opt)} value={String(opt)}>
                                    {getEnumLabel(itemSchema, index, opt)}
                                </option>
                            ))}
                        </Select>
                    );
                },
                showOnView: true,
            },
            {
                // Free-text fields with suggested values (e.g. theme.headerFonts) — the
                // schema only lists x-popular-options/x-all-options as suggestions, not
                // an enum, so any value must stay editable, not restricted to a Select.
                condition: ({ path }: { path: (string | number)[] }) => {
                    const node = resolveSchemaNode(schema, path);
                    return (
                        !node?.enum &&
                        (Array.isArray(node?.['x-popular-options']) ||
                            Array.isArray(node?.['x-all-options']))
                    );
                },
                element: ({ value, handleEdit, canEdit, nodeData }: any) => {
                    const node = resolveSchemaNode(schema, nodeData.path);
                    const options: string[] =
                        (node?.['x-all-options'] as string[]) ??
                        (node?.['x-popular-options'] as string[]) ??
                        [];
                    ensureGoogleFontsLoaded(options);
                    const current = String(value ?? '');
                    // Not a real enum — the current value may be outside the suggested
                    // list, so keep it selectable rather than silently swapping it out.
                    const selectOptions =
                        !current || options.includes(current) ? options : [current, ...options];
                    const fontSelectOptions = selectOptions.map((opt) => ({
                        value: opt,
                        label: extractFamilyName(opt),
                    }));
                    const minWidthCh = Math.max(
                        ...fontSelectOptions.map((opt) => formatFontMenuLabel(opt.label).length)
                    );
                    return (
                        <RichSelect
                            isDisabled={!canEdit}
                            options={fontSelectOptions}
                            value={current ? { value: current, label: extractFamilyName(current) } : null}
                            styles={{
                                control: (base: object) => ({ ...base, minWidth: `${minWidthCh}ch` }),
                                menu: (base: object) => ({ ...base, minWidth: `${minWidthCh}ch` }),
                                option: (base: object) => ({ ...base, fontSize: '16px' }),
                            }}
                            formatOptionLabel={(
                                option: { value: string; label: string },
                                { context }: { context: 'menu' | 'value' }
                            ) => (
                                <span style={{ fontFamily: option.value }}>
                                    {context === 'menu'
                                        ? formatFontMenuLabel(option.label)
                                        : option.label}
                                </span>
                            )}
                            onChange={(option: { value: string; label: string } | null) =>
                                handleEdit(option?.value ?? '')
                            }
                        />
                    );
                },
                showOnView: true,
            },
        ]
        : [];

    return (
        <Field name={name} validate={validateConfigurationJson}>
            {({ field, form, meta }: any) => {
                let parsed: unknown = null;
                let parseError = false;
                try {
                    parsed = field.value ? JSON.parse(field.value) : {};
                } catch {
                    parseError = true;
                }

                const primaryColor = !parseError
                    ? ((parsed as Record<string, any>)?.theme?.light?.primaryColor as string | undefined)
                    : undefined;

                const handleCopyFullScript = async () => {
                    if (parseError) return;
                    const embedScript = buildFullEmbedScript({
                        embedScriptUrl: embedScriptUrl!,
                        companySitepath: companySitepath!,
                        configId: configId!,
                        configuration: parsed,
                        fitViewport: fitViewportEnabled,
                    });
                    await navigator.clipboard.writeText(embedScript);
                    setCopiedFullScript(true);
                    setTimeout(() => setCopiedFullScript(false), COPIED_FEEDBACK_MS);
                };

                return (
                    <Box sx={{ width: '100%' }}>
                        {canPreview && (
                            <Flex sx={{ justifyContent: 'flex-end', gap: 2, mb: 2 }}>
                                <Button
                                    variant="secondary"
                                    type="button"
                                    onClick={() => setShowPreview((s) => !s)}
                                >
                                    {showPreview ? 'Hide preview' : 'Live preview'}
                                </Button>
                                {canCopyFullScript && (
                                    <Button
                                        variant="secondary"
                                        type="button"
                                        disabled={parseError}
                                        onClick={handleCopyFullScript}
                                    >
                                        {copiedFullScript ? 'Copied!' : 'Copy full script'}
                                    </Button>
                                )}
                            </Flex>
                        )}
                        <Flex sx={{ gap: 3, alignItems: 'flex-start', width: '100%' }}>
                            <ColorHelperPanel initialColor={primaryColor} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <JsonFieldViewer
                                    label="Configuration (JSON)"
                                    required
                                    parseError={parseError}
                                    getCopyText={() =>
                                        parseError ? undefined : JSON.stringify(parsed, null, 2)
                                    }
                                    error={
                                        meta.touched && meta.error ? (
                                            <Text color="danger">{meta.error}</Text>
                                        ) : null
                                    }
                                    rawContent={
                                        <Textarea
                                            {...field}
                                            backgroundColor="gray.1"
                                            rows={rows}
                                            sx={{
                                                maxWidth: '100%',
                                                minWidth: '100%',
                                                fontFamily: 'monospace',
                                                fontSize: 1,
                                            }}
                                            placeholder={placeholder}
                                        />
                                    }
                                    treeContent={
                                        <JsonEditor
                                            data={parsed as object}
                                            setData={(newData: unknown) => {
                                                form.setFieldValue(name, JSON.stringify(newData, null, 2));
                                            }}
                                            customNodeDefinitions={customNodeDefinitions}
                                        />
                                    }
                                />
                            </Box>
                            {canPreview && showPreview && (
                                <Box sx={{ flex: '0 0 420px', minWidth: 0 }}>
                                    <WidgetPreviewPane
                                        configuration={parseError ? null : (parsed as Record<string, unknown>)}
                                        companySitepath={companySitepath!}
                                        embedScriptUrl={embedScriptUrl!}
                                        fitViewport={fitViewportEnabled}
                                    />
                                </Box>
                            )}
                        </Flex>
                    </Box>
                );
            }}
        </Field>
    );
};

export default ConfigurationJsonField;
